import React, { FunctionComponent, useCallback, useRef } from 'react'
import { FormattedMessage } from 'react-intl'

import BottomSheetGorhom from '@gorhom/bottom-sheet'
import styled, { useTheme } from 'styled-components/native'

import {
  CALENDAR_HYBRID_AUTH_HELP_LINK_DA,
  CALENDAR_HYBRID_AUTH_TICKET_LINK_OUS,
} from '@lyrahealth-inc/shared-app-logic/src/features/calendar/provider/constants'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { BulletedList } from '../../atoms/bulletedList/BulletedList'
import { XIcon } from '../../atoms/icons/XIcon'
import { PermissionsIllustration } from '../../atoms/illustrations/PermissionsIllustration'
import { Link } from '../../atoms/link/Link'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { PrimaryButton } from '../../atoms/primaryButton/PrimaryButton'
import { TextButton } from '../../atoms/textButton/TextButton'
import { Title } from '../../atoms/title/Title'
import { Modal } from '../../organisms/modal/Modal'
import { TitleSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

type ProviderHybridCalendarAuthorizationModalProps = {
  onAuthorizePressed?: () => void
  onCancelPressed?: () => void
  isVisible?: boolean
  useInternationalLinks?: boolean
}

const Container = styled.View({
  flexDirection: 'column',
  alignSelf: 'flex-start',
  alignItems: 'center',
})

const Header = styled.View({
  flexDirection: 'column',
  alignItems: 'center',
})

const Row = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  gap: theme.spacing['16px'],
  alignSelf: 'flex-end',
  alignItems: 'center',
  marginTop: theme.spacing['32px'],
}))

const LinkButton = styled(TextButton)({
  alignSelf: 'center',
})

const HeaderText = styled(Title)(({ theme }) => ({
  marginTop: theme.spacing['24px'],
}))

const TipsHeaderText = styled(Title)(({ theme }) => ({
  marginTop: theme.spacing['16px'],
  alignSelf: 'flex-start',
}))

const CloseButton = styled(PressableOpacity)({
  alignSelf: 'flex-end',
})

export const ProviderHybridCalendarAuthorizationModal: FunctionComponent<
  ProviderHybridCalendarAuthorizationModalProps
> = ({ onAuthorizePressed, onCancelPressed, isVisible = false, useInternationalLinks = false }) => {
  const theme = useTheme() as ThemeType
  const ref = useRef<BottomSheetGorhom>(null)
  const openBottomSheet = useCallback(() => {
    ref.current?.expand()
  }, [])

  return (
    <Modal
      visible={isVisible}
      bottomSheetRef={ref}
      snapPoints={[550]}
      width='600px'
      onLayout={openBottomSheet}
      modalContents={
        <Container>
          <CloseButton hitSlop={60} onPress={() => onCancelPressed?.()}>
            <XIcon size={24} fillColor={theme.colors.iconDefault} />
          </CloseButton>
          <Header>
            <PermissionsIllustration />
            <HeaderText
              textAlign='center'
              size={TitleSize.MEDIUM}
              text={
                <FormattedMessage
                  defaultMessage='Connect your Google Calendar'
                  description='Subheader for provider Google Calendar authorization card'
                />
              }
            />
            <BodyText
              textAlign='center'
              color={theme.colors.textSecondary}
              text={
                <FormattedMessage
                  defaultMessage='You’ll be prompted to connect your Google account with Lyra to enable sync.'
                  description='Body text describing provider Google Calendar authorization process'
                />
              }
            />
          </Header>
          <TipsHeaderText
            size={TitleSize.XSMALL}
            text={
              <FormattedMessage
                defaultMessage='Tips'
                description='Subheader for tips section on provider Google Calendar authorization card'
              />
            }
            color={theme.colors.textSecondary}
          />
          <BulletedList
            spaceBetween={16}
            textColor={theme.colors.textSecondary}
            bulletColor={theme.colors.textSecondary}
            data={[
              {
                value: (
                  <BodyText
                    color={theme.colors.textSecondary}
                    text={
                      <FormattedMessage
                        defaultMessage='Initial setup requires that you connect a Google Account calendar.'
                        description='Bullet point describing what is required for calendar setup'
                      />
                    }
                  />
                ),
              },
              {
                value: (
                  <BodyText
                    color={theme.colors.textSecondary}
                    text={
                      <FormattedMessage
                        defaultMessage='Don’t see your preferred Google Account on the next page? Click <bold>“Use another account”</bold> and log in to the desired one.'
                        description='Bullet point describing how to select the proper Google account during authorization'
                        values={{
                          bold: (text: string) => (
                            <BodyText bold color={theme.colors.textSecondary}>
                              {text}
                            </BodyText>
                          ),
                        }}
                      />
                    }
                  />
                ),
              },
              {
                value: (
                  <BodyText
                    color={theme.colors.textSecondary}
                    text={
                      <FormattedMessage
                        defaultMessage='If you do not have a Google Account, sign up for free by clicking <bold>“Create Account”</bold> on the next screen.'
                        description='Bullet point describing how to create a Google account during authorization'
                        values={{
                          bold: (text: string) => (
                            <BodyText bold color={theme.colors.textSecondary}>
                              {text}
                            </BodyText>
                          ),
                        }}
                      />
                    }
                  />
                ),
              },
              {
                value: (
                  <BodyText
                    color={theme.colors.textSecondary}
                    text={
                      <FormattedMessage
                        defaultMessage='(Optional) After you link a Google Account, you can sync your appointment data from other systems (ex. Simple Practice, iCloud, Outlook, etc.). Visit our <link>Help Center</link> for more instructions.'
                        description='Bullet point describing the option to sync other appointment data after Google Authorization'
                        values={{
                          link: (text: string) => (
                            <Link
                              text={text}
                              testID={tID('ProviderHybridCalendarAuthorizationModal-help-link')}
                              //TODO: Will eventually need a Mixpanel event: DACN-1336
                              onPress={() =>
                                window.open(
                                  useInternationalLinks
                                    ? CALENDAR_HYBRID_AUTH_TICKET_LINK_OUS
                                    : CALENDAR_HYBRID_AUTH_HELP_LINK_DA,
                                  '_blank',
                                )
                              }
                            />
                          ),
                        }}
                      />
                    }
                  />
                ),
              },
            ]}
          />
          <Row>
            <LinkButton
              text={
                <FormattedMessage defaultMessage='Cancel' description='Button to cancel authorization of calendar' />
              }
              onPress={() => onCancelPressed?.()}
            />
            <PrimaryButton
              testID={tID('ProviderHybridCalendarAuthorizationModal-authorize-button')}
              text={<FormattedMessage defaultMessage='Connect' description='Button to connect Google calendar' />}
              onPress={() => onAuthorizePressed?.()}
            />
          </Row>
        </Container>
      }
      onRequestClose={() => onCancelPressed?.()}
      onCloseEnd={() => onCancelPressed?.()}
    />
  )
}
